/**
 * Context, hooks and types shared inside the Input component family.
 *
 * Separate from `input.tsx` for the same reason as `input.constants.ts`: a module
 * that exports both components and plain values trips
 * `react-refresh/only-export-components`. That rule is OFF in this repo, so our own
 * lint never sees it — but the registry is VENDORED, and consumers who enable it
 * (gaia does) get a permanent warning in a file they are told not to hand-edit.
 * Re-vendoring the Input family into gaia produced exactly that: four warnings on
 * `input.tsx`, two per hook, which no consumer could clear.
 *
 * This file stays `.ts` rather than `.tsx` — nothing here needs JSX, and the rule
 * only inspects files that could hold a component. `InputFieldProvider` is a bare
 * re-export of `Context.Provider`, which is a value rather than a declared
 * component, so it travels with the context it belongs to.
 *
 * Everything here is re-exported from `index.ts`, so consumers importing from the
 * barrel need no change.
 */

import * as React from 'react';

/**
 * Shared ids for one Input group, so the label, the control and the helper agree
 * without the consumer wiring them by hand at every call site.
 *
 * This is plumbing, not behavior: it carries two generated ids and a mount
 * counter. No value, no onChange, nothing intercepted — the control remains a
 * plain native `<input>`. An explicit `id` / `htmlFor` / `aria-describedby` from
 * the consumer always wins, and every primitive still works standalone outside a
 * group (it simply gets no auto-wiring).
 *
 * `describedByIds` exists so `InputField` only advertises `aria-describedby` when a
 * helper is actually mounted. Pointing at an id that resolves to nothing is
 * worse than pointing at nothing: it promises a description that does not exist
 * and trips axe's `aria-valid-attr-value`.
 */
export type InputFieldRegistration = {
  controlId: string;
  controlRef: React.MutableRefObject<InputGroupControl | null>;
  /**
   * The control's own `disabled` / `readOnly`, as props rather than as read off the
   * DOM node.
   *
   * A node read looks stronger and is not: `HTMLInputElement.disabled` reflects the
   * content attribute only, so it does not see `<fieldset disabled>` inheritance —
   * the one case a prop could not reach is a case the node misses too. What a prop
   * does give is a reactive value, which is what keeps a slot in step when the field
   * is toggled.
   *
   * Kept fresh through `updateField`, not through re-registration: registration is
   * append-and-remove by identity, so re-running it on a toggle would move the field
   * to the end of the list and hand the primary to a peer.
   */
  disabled: boolean;
  readOnly: boolean;
};

/**
 * The validation / edit states a group can declare, matching the root classes in
 * `input.css`. `dirty` and `new` paint a border and carry no glyph; only `error` and
 * `warning` have one.
 */
export type InputState = 'error' | 'warning' | 'dirty' | 'new';

type InputGroupContextValue = {
  /**
   * The id of the group's PRIMARY control — the first `InputField` to register.
   * `InputLabel` points `htmlFor` here.
   *
   * Derived from registration rather than minted once per group. A single id per
   * group meant a second `InputField` (a min/max pair is the obvious case) emitted
   * a duplicate DOM id, so the label resolved to whichever the browser found first
   * and the two controls were indistinguishable to assistive tech.
   */
  controlId: string | undefined;
  /** Effective ids of the mounted helpers, in the order they registered. */
  describedByIds: string[];
  registerHelper: (id: string) => () => void;
  /**
   * The effective id of the group's first mounted `InputLabel`, or `undefined` when
   * there is none.
   *
   * `htmlFor` names a native control, but it cannot name anything else: a popup a
   * button-shaped field opens (a DropdownInput's menu) needs `aria-labelledby`
   * pointing at the label itself, and a button that shows a value needs the label
   * and the value together. Registered like helpers, so a consumer's own `id` on the
   * label is the one published.
   */
  labelId: string | undefined;
  registerLabel: (id: string) => () => void;
  /**
   * The PRIMARY control, for group-level slots that have no field of their own.
   * Slots rendered INSIDE a field read `InputFieldContext` instead, so they act on
   * the field that contains them rather than on whichever one mounted last.
   */
  controlRef: React.MutableRefObject<InputGroupControl | null> | undefined;
  /**
   * Announce a field and its control. Returns an unregister callback. Order is
   * registration order, so `[0]` is the primary and a later field is a peer rather
   * than an overwrite.
   */
  registerField: (field: InputFieldRegistration) => () => void;
  /** The PRIMARY control's `disabled` / `readOnly`, for group-level slots. */
  controlDisabled: boolean;
  controlReadOnly: boolean;
  /**
   * Patch an already-registered field in place, by `controlId`. The reactive half of
   * `registerField`: a field's `disabled` / `readOnly` change while it stays mounted,
   * and re-registering to carry the new values would reorder the list.
   */
  updateField: (controlId: string, patch: Partial<InputFieldRegistration>) => void;
  /**
   * The group's declared state, from `Input`'s `state` prop.
   *
   * `undefined` when the consumer styles with `className="input-error"` instead —
   * that stays valid and is unaffected, it simply does not reach here. Nothing parses
   * `className`: it arrives already merged, in arbitrary order, and a consumer class
   * can contain one of these names as a substring.
   */
  state: InputState | undefined;
};

/**
 * The control a group can host. `Textarea` composes this same group so it inherits
 * the label / helper / aria-describedby wiring rather than reimplementing it, so the
 * ref has to admit a textarea too — and a button-shaped field (`SelectTrigger`,
 * `DropdownInputTrigger`) joins it the same way, so it admits a button.
 *
 * Code holding one should narrow before reading anything a button lacks
 * (`readOnly`, `select()`, `setSelectionRange`): `InputStepper` checks for an
 * `<input>`, and `InputClear` only ever calls `focus()`.
 */
export type InputGroupControl = HTMLInputElement | HTMLTextAreaElement | HTMLButtonElement;

/**
 * Exported so `input.tsx` can mount the Provider and read it. NOT re-exported from
 * `index.ts` — the supported seam is `useInputGroup()`, whose JSDoc explains why the
 * value's shape is not a stable contract.
 */
export const InputGroupContext = React.createContext<InputGroupContextValue | null>(null);

/**
 * The enclosing `InputField`'s own control — its id and its ref.
 *
 * Separate from the group context because a group can hold several fields. Anything
 * that means "the control next to me" (`InputClear`'s focus handback) must resolve
 * through the field it is rendered in; only things that mean "the field this group
 * is labelled by" (`InputLabel`) go through the group.
 */
/** Exported for `input.tsx` only; consumers use `InputFieldProvider` / `useInputFieldControl`. */
export const InputFieldContext = React.createContext<InputFieldRegistration | null>(null);

/**
 * Publish a field's own control to its children. The other half of the seam
 * `useInputGroup` opens: a field type joins the group for label / helper wiring, and
 * wraps its slots in this so anything meaning "the control next to me" resolves to
 * ITS control rather than to the group's primary. `TextareaField` is the worked
 * example.
 */
export const InputFieldProvider = InputFieldContext.Provider;

/**
 * Read the enclosing field's own control, or `null` outside one. `InputClear` uses
 * this and falls back to the group's primary, which is what keeps it correct both
 * inside a field and in a hand-built composition.
 */
export function useInputFieldControl() {
  return React.useContext(InputFieldContext);
}

/**
 * Read the enclosing `Input` group. This is the supported seam for building another
 * field type on the Input family: join the group and you inherit the label
 * association, the `aria-describedby` list and every root state modifier, instead of
 * standing up a parallel implementation. `Textarea` is the worked example.
 *
 * Returns `null` outside a group — every consumer must tolerate that, since the
 * field types are all usable standalone.
 *
 * The HOOK and `InputGroupControl` are public. The **shape** of the value it returns
 * is not: fields get added to it (`registerField` was the most recent), and treating
 * it as a stable contract would make every such addition breaking. Read the
 * properties you need and do not destructure exhaustively.
 */
export function useInputGroup() {
  return React.useContext(InputGroupContext);
}

/**
 * Join the enclosing group as a field, and keep the group's copy of this control's
 * `disabled` / `readOnly` current.
 *
 * The seam every field type in this family goes through, so the registration contract
 * and the reasoning below live in one place rather than once per field type.
 *
 * A layout effect, not an effect: it runs before paint, so a label is never painted
 * with a dangling `htmlFor`. Keyed on `registerField` rather than on the group, whose
 * identity changes every time a helper mounts — re-running then would re-append this
 * field and shuffle the primary.
 *
 * `disabled` and `readOnly` are deliberately NOT dependencies of the registration.
 * Registration appends and unregistration removes by identity, so re-running it on a
 * toggle would move this field to the end of the list and hand the primary to a peer.
 * The second effect patches the values in place instead, which is what keeps a slot
 * reading them in step with the field.
 */
export function useFieldRegistration({
  controlId,
  controlRef,
  disabled,
  readOnly,
}: InputFieldRegistration) {
  const group = React.useContext(InputGroupContext);
  const registerField = group?.registerField;
  const updateField = group?.updateField;

  React.useLayoutEffect(
    () => registerField?.({ controlId, controlRef, disabled, readOnly }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerField, controlId]
  );

  React.useLayoutEffect(() => {
    updateField?.(controlId, { disabled, readOnly });
  }, [updateField, controlId, disabled, readOnly]);
}

export type GroupFieldOptions<T extends InputGroupControl> = {
  /** The consumer's `id`, which replaces the generated one (an element has one). */
  id?: string;
  /** The component's forwarded ref, kept working alongside the field's own handle. */
  ref: React.ForwardedRef<T>;
  disabled: boolean;
  readOnly: boolean;
  /** The consumer's own `aria-describedby`, merged with the group's helpers. */
  ariaDescribedBy?: string;
};

/**
 * Everything a field type needs to join its `Input` group, in one place:
 * `InputField`, `TextareaField`, `SelectTrigger` and `DropdownInputTrigger` all use
 * it, so the rules below cannot drift between them.
 *
 * - **Its own id and control handle.** A group can hold several fields; sharing the
 *   group's id or ref meant a second field emitted a duplicate DOM id and overwrote
 *   the first field's handle, so click-to-focus and `InputClear` hit the wrong one.
 *   A consumer's `id` replaces the generated one.
 * - **Registration** through `useFieldRegistration`, so `InputLabel` points at it.
 * - **A merged description.** `aria-describedby` is an id LIST: the consumer's own
 *   ids come first, then the group's helpers in registration order. Replacing
 *   instead meant pointing at an external error silently stopped the helper being
 *   announced, in exactly the case where the user needs both.
 *
 * Attach `setControlRef` as the element's `ref`, `controlId` as its `id` and
 * `describedBy` as its `aria-describedby`. `field` is the value to publish through
 * `InputFieldProvider`, for slots that mean "the control next to me". Returns the
 * group too (or `null` outside one), for anything else the field reads from it.
 */
export function useGroupField<T extends InputGroupControl>({
  id,
  ref,
  disabled,
  readOnly,
  ariaDescribedBy,
}: GroupFieldOptions<T>) {
  const group = React.useContext(InputGroupContext);

  const ownId = React.useId();
  const controlId = id ?? ownId;

  const controlRef = React.useRef<T | null>(null);
  const setControlRef = React.useCallback(
    (node: T | null) => {
      controlRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  useFieldRegistration({ controlId, controlRef, disabled, readOnly });

  const describedBy =
    [ariaDescribedBy, ...(group?.describedByIds ?? [])].filter(Boolean).join(' ') || undefined;

  const field = React.useMemo<InputFieldRegistration>(
    () => ({ controlId, controlRef, disabled, readOnly }),
    [controlId, disabled, readOnly]
  );

  return { group, controlId, controlRef, setControlRef, describedBy, field };
}
